import { CopyCellText as CopyText } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CopyCellText';
import {
	EllipsizedHeader,
	isCapTableRowSelected,
	MipGroupBadge,
	TableHeaderCellWithHoverContext,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableElements';
import { CAP_TABLE_VALUES_HELD_TYPE } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings';
import {
	checkIsIpGroupExpanded,
	extractEntityIdFromReceiver,
	getCellValue,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableUtils';
import { EntityTags } from '@dealstack/frontend/app/pages/Project/Cap/common/EntityTags/EntityTags';
import { UndefinedItalic } from '@dealstack/frontend/shared/other/react/specialSigns';
import { Flex } from '@mantine/core';
import { CellContext, HeaderContext } from '@tanstack/react-table';
import { compact, Dictionary } from 'lodash';

import { CapTableColumn, CapTableRow } from './capTableTypes';
import styled from 'styled-components';
import { generatePath, Link } from 'react-router-dom';
import React from 'react';
import { Receiver } from '@dealstack/shared/lib/generated';
import { RowIdentificationKey } from '@dealstack/shared/lib/common/business/cap/capTable.types';
import { checkIsNaN } from '@dealstack/shared/lib/checkIsNaN.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { TableCellWithEvents } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/TableCellWithEvents.tsx';
import { isSecurityIssuedInCurrency } from '@dealstack/frontend/shared/cap.ts';
import {
	GENERIC_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds.ts';
import { DEAL_ROUTES, PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';

export const mapReceiverToColumn = ({
	receiver,
	valuesHeldType,
	showSummarized,
	showPercentages,
}: {
	receiver: Receiver;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	showSummarized: boolean;
	showPercentages: boolean;
}) => {
	const color = receiver.color;
	const isIpColumn = 'isIPColumn' in receiver;
	const isInvestorUnderIp = !isIpColumn && !!receiver.ipId;

	const generatePersonPath = (personId: string) => {
		return generatePath(`..${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`, {
			personId,
		});
	};

	return {
		id: receiver.id,
		enableHiding: true,
		header: ({ table }: HeaderContext<CapTableRow, unknown>) => {
			const personId = extractEntityIdFromReceiver(receiver.id);

			if (!receiver.isIPColumn) {
				return (
					<TableHeaderCellWithHoverContext
						color={color}
						$showBottomBorder={isInvestorUnderIp}
						$showLeftBorder={!isInvestorUnderIp}
						columnId={receiver.id}
					>
						<Flex direction="column" justify="space-between" gap="1rem" h="100%">
							<EllipsizedHeader
								short={
									<div>
										<StyledLink to={generatePersonPath(personId)}>
											{receiver.name}
										</StyledLink>
										{receiver.heldForId && (
											<>
												<br />
												<span> (for </span>
												<StyledLink
													to={generatePersonPath(receiver.heldForId)}
												>
													{receiver.heldForName})
												</StyledLink>
											</>
										)}
									</div>
								}
								full={
									<div>
										<div style={{ marginBottom: '0.25rem' }}>
											<StyledLink to={generatePersonPath(personId)}>
												{receiver.name}
											</StyledLink>
										</div>
										{receiver.heldForId && (
											<div>
												for&nbsp;
												<StyledLink
													to={generatePersonPath(receiver.heldForId)}
												>
													{receiver.heldForName}
												</StyledLink>
											</div>
										)}
									</div>
								}
							/>

							<EntityTags
								tags={receiver.tags || []}
								visibleTagCount={1}
								blockDropdown={true}
							/>
						</Flex>
					</TableHeaderCellWithHoverContext>
				);
			}

			const isIpGroupExpanded = checkIsIpGroupExpanded(receiver, table);

			const onExpandToggle = () => {
				table.setColumnVisibility((prevState) => {
					const stateCopy = { ...prevState };
					(receiver || {}).investorsKeys?.forEach(
						(key: string) => (stateCopy[key] = !isIpGroupExpanded)
					);
					return stateCopy;
				});
			};

			return (
				<TableHeaderCellWithHoverContext
					color={color}
					$showBottomBorder={isIpColumn || isInvestorUnderIp}
					$showLeftBorder={!isInvestorUnderIp}
					columnId={receiver.id}
				>
					<Flex direction="column" justify="space-between" gap="1rem" h="100%">
						<Flex gap="1rem" justify="space-between">
							<EllipsizedHeader short={receiver.name} full={receiver.name} />

							<div data-html2canvas-ignore={true} style={{ width: '1rem' }}>
								<Icon
									onClick={onExpandToggle}
									size="lg"
									variant={
										isIpGroupExpanded
											? 'collapse-vertically'
											: 'expand-vertically'
									}
									className="expand-toggle-icon"
								/>
							</div>
						</Flex>

						<MipGroupBadge />
					</Flex>
				</TableHeaderCellWithHoverContext>
			);
		},
		cell: ({ row, table }: CellContext<CapTableRow, unknown>) => {
			if (isIpColumn && receiver.isIPColumn && checkIsIpGroupExpanded(receiver, table)) {
				return;
			}

			const rowData = row.original;
			const isRowExpanded = row.getIsExpanded();
			const isRowSelected = isCapTableRowSelected(table, row);
			const isInSelectionMode = showPercentages;

			const isEntityRow = RowIdentificationKey.issuerSummarizedHolders in rowData;
			if (isEntityRow) {
				if (isRowExpanded && !showPercentages) {
					return;
				}
				const holderData = rowData.issuerSummarizedHolders[receiver.id];
				if (!holderData) {
					return;
				}

				const value = getCellValue({
					holderValues: holderData.strict,
					valuesHeldType,
					showSummarized,
					showPercentages,
				});
				if (checkIsNaN(value)) {
					return UndefinedItalic;
				}
				if (!value) {
					return;
				}
				return <CopyText text={value}>{value}</CopyText>;
			}

			const isTypeRow = RowIdentificationKey.securityTypeSummarizedHolders in rowData;
			if (isTypeRow) {
				if (
					(isRowExpanded && !showPercentages) ||
					isSecurityIssuedInCurrency(rowData.securityIssuedIn)
				) {
					return;
				}
				const holderData = rowData.securityTypeSummarizedHolders[receiver.id];
				if (!holderData) {
					return;
				}

				const value = getCellValue({
					holderValues: holderData.strict,
					currency: rowData.currency,
					securityIssuedIn: rowData.securityIssuedIn,
					valuesHeldType,
					showSummarized,
					showPercentages,
				});
				if (checkIsNaN(value)) {
					return UndefinedItalic;
				}
				if (!value) {
					return;
				}
				return <CopyText text={value}>{isRowSelected ? value : ''}</CopyText>;
			}

			const isSecurityRow = RowIdentificationKey.securityHolders in rowData;
			if (isSecurityRow) {
				const holderData = rowData.securityHolders[receiver.id];
				if (!holderData) {
					return;
				}

				const value = getCellValue({
					holderValues: holderData.strict,
					currency: rowData.currency,
					securityIssuedIn: rowData.securityIssuedIn,
					valuesHeldType,
					showSummarized,
					showPercentages,
				});
				if (checkIsNaN(value)) {
					return UndefinedItalic;
				}
				if (!value) {
					return;
				}

				const holderEventFlags = rowData.securityEventsFlags?.[receiver.id];
				const hasAssignment = holderEventFlags?.hasAssignment || false;
				const hasPledge = holderEventFlags?.hasPledge || false;

				const getValueDisplay = () => {
					if (isInSelectionMode) {
						return isRowSelected ? value : '';
					}
					return value;
				};

				if (hasAssignment || hasPledge) {
					const buildHoldingsLink = () => {
						const { securityId, issuerId } = rowData;
						const holderId = receiver.id;

						return generatePath(
							`..${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.HOLDING_DETAILS}?securityId=${securityId}&holderId=${holderId}`,
							{
								personId: issuerId,
							}
						);
					};

					return (
						<TableCellWithEvents
							hasAssignment={hasAssignment}
							hasPledge={hasPledge}
							value={getValueDisplay()}
							copyable={true}
							buildHoldingsLink={buildHoldingsLink}
						/>
					);
				}

				return (
					<CopyText
						contentDataTestId={makeComplexDataTestId(
							GENERIC_ELEMENT_ID.MANTINE_TABLE_CELL,
							receiver.name
						)}
						text={value}
					>
						{getValueDisplay()}
					</CopyText>
				);
			}
			return;
		},
	};
};

export const mapIpGroupsReceiversToColumns = ({
	ipGroupsReceiversDictionary,
	valuesHeldType,
	showSummarized,
	showPercentages,
}: {
	ipGroupsReceiversDictionary: Dictionary<Receiver[]>;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	showSummarized: boolean;
	showPercentages: boolean;
}): Dictionary<CapTableColumn[]> => {
	const ipGroupsReceiversColumns: Dictionary<CapTableColumn[]> = {};

	for (const ipId in ipGroupsReceiversDictionary) {
		const ipGroupReceivers = ipGroupsReceiversDictionary[ipId];
		const receiversColumns = compact(
			ipGroupReceivers?.map((receiver) =>
				mapReceiverToColumn({
					receiver,
					valuesHeldType,
					showSummarized,
					showPercentages,
				})
			)
		);
		ipGroupsReceiversColumns[ipId] = receiversColumns;
	}

	return ipGroupsReceiversColumns;
};

export const insertIpGroupReceiversColumnsAfterIpColumn = (
	columns: CapTableColumn[],
	ipGroupsReceiversColumns: Dictionary<CapTableColumn[]>,
	ipsIds: string[]
): CapTableColumn[] => {
	return columns
		.map((column) => {
			const receiverId = column.id;
			if (!receiverId) {
				return column;
			}

			const isIpId = ipsIds.includes(receiverId);
			if (!isIpId) {
				return column;
			}

			const ipGroupReceiversColumns = ipGroupsReceiversColumns[receiverId];
			if (!ipGroupReceiversColumns) {
				return column;
			}

			return [column, ...ipGroupReceiversColumns];
		})
		.flat();
};

const StyledLink = styled(Link)`
	color: inherit;
	text-decoration: none; /* Remove default underline */

	&:hover {
		color: ${(props) => props.theme.v3.colors.blue[6]}; /* Apply blue color */
		text-decoration: underline; /* Add underline */
	}
`;
